import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertTriangle, Info, Settings, BarChart3, DollarSign, Users, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const KPIs = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    normalization: {
      base: 'revenue',
      currency: 'USD',
      baseYear: 2023
    },
    kpis: [],
    customKpis: [],
    assumptions: [],
    ...initialData
  });

  // Load initial data when component mounts or initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Auto-save to localStorage whenever formData changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      try {
        const currentData = JSON.parse(localStorage.getItem('calculationWorkflowData') || '{}');
        const updatedData = {
          ...currentData,
          kpis: formData
        };
        localStorage.setItem('calculationWorkflowData', JSON.stringify(updatedData));
      } catch (error) {
        console.error('Error saving KPIs data to localStorage:', error);
      }
    }
  }, [formData]);

  const [errors] = useState({});
  const [warnings] = useState({});

  // Sample KPI data
  const sampleKPIs = React.useMemo(() => [
    {
      id: 1,
      name: 'Energy Intensity',
      type: 'energy',
      description: 'Energy consumption per unit of output',
      unit: 'kWh/product',
      currentValue: 1250,
      baseYearValue: 1350,
      target: 1000,
      targetYear: 2025,
      trend: [1350, 1300, 1280, 1250],
      years: [2020, 2021, 2022, 2023],
      normalizationBase: 'product',
      dataAvailable: true,
      assumption: ''
    },
    {
      id: 2,
      name: 'Carbon Intensity',
      type: 'carbon',
      description: 'Emissions per unit of revenue',
      unit: 'tCO₂e/USD',
      currentValue: 0.00085,
      baseYearValue: 0.00092,
      target: 0.00070,
      targetYear: 2025,
      trend: [0.00092, 0.00089, 0.00087, 0.00085],
      years: [2020, 2021, 2022, 2023],
      normalizationBase: 'revenue',
      dataAvailable: true,
      assumption: ''
    },
    {
      id: 3,
      name: 'Health & Safety - Injury Rate',
      type: 'hs',
      description: 'Injury rate per 200,000 hours worked',
      unit: 'injuries/200k hours',
      currentValue: 2.1,
      baseYearValue: 2.8,
      target: 1.5,
      targetYear: 2025,
      trend: [2.8, 2.5, 2.3, 2.1],
      years: [2020, 2021, 2022, 2023],
      normalizationBase: 'hours',
      dataAvailable: true,
      assumption: ''
    },
    {
      id: 4,
      name: 'Energy per Revenue',
      type: 'energy',
      description: 'Energy consumption per unit of revenue',
      unit: 'kWh/USD',
      currentValue: 0.45,
      baseYearValue: 0.52,
      target: 0.35,
      targetYear: 2025,
      trend: [0.52, 0.49, 0.47, 0.45],
      years: [2020, 2021, 2022, 2023],
      normalizationBase: 'revenue',
      dataAvailable: false,
      assumption: 'Revenue data estimated based on industry averages'
    }
  ], []);

  // Initialize with sample data if empty
  useEffect(() => {
    if (!formData.kpis || formData.kpis.length === 0) {
      setFormData(prev => ({
        ...prev,
        kpis: sampleKPIs
      }));
    }
  }, [formData.kpis, sampleKPIs]);

  const handleNormalizationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      normalization: {
        ...prev.normalization,
        [field]: value
      }
    }));
  };



  const handleAssumptionChange = (kpiId, assumption) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.map(kpi => 
        kpi.id === kpiId 
          ? { ...kpi, assumption }
          : kpi
      )
    }));
  };

  const addCustomKPI = () => {
    const newCustomKPI = {
      id: Date.now(),
      name: 'Custom KPI',
      type: 'custom',
      description: 'Custom performance indicator',
      unit: 'unit/metric',
      currentValue: 0,
      baseYearValue: 0,
      target: 0,
      targetYear: 2025,
      trend: [],
      years: [],
      normalizationBase: 'revenue',
      dataAvailable: true,
      assumption: '',
      isCustom: true
    };

    setFormData(prev => ({
      ...prev,
      kpis: [...prev.kpis, newCustomKPI]
    }));
  };

  const removeCustomKPI = (kpiId) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.filter(kpi => kpi.id !== kpiId)
    }));
  };

  const getNormalizationOptions = () => [
    { value: 'revenue', label: 'Revenue', icon: DollarSign },
    { value: 'product', label: 'Product Output', icon: Package },
    { value: 'fte', label: 'Full-Time Employees', icon: Users },
    { value: 'hours', label: 'Hours Worked', icon: TrendingUp }
  ];

  const getCurrencyOptions = () => [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'PLN', label: 'PLN (zł)' }
  ];

  const getTrendColor = (current, base) => {
    if (current < base) return 'text-green-600';
    if (current > base) return 'text-red-600';
    return 'text-gray-600';
  };

  const getTrendIcon = (current, base) => {
    if (current < base) return '↘️';
    if (current > base) return '↗️';
    return '→';
  };





  const getErrorClass = (kpiId) => {
    return errors[kpiId] ? 'border-red-500 bg-red-50' : '';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
          <TrendingUp className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('workflow.calculation.kpis.title')}</h2>
          <p className="text-gray-600">{t('workflow.calculation.kpis.description')}</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Normalization Settings */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Settings className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-blue-900">{t('workflow.calculation.kpis.normalization.title')}</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('workflow.calculation.kpis.normalization.normalizationBase')}</label>
              <select
                value={formData.normalization.base}
                onChange={(e) => handleNormalizationChange('base', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.normalization ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {getNormalizationOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('workflow.calculation.kpis.normalization.baseCurrency')}</label>
              <select
                value={formData.normalization.currency}
                onChange={(e) => handleNormalizationChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {getCurrencyOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('workflow.calculation.kpis.normalization.baseYear')}</label>
              <input
                type="number"
                value={formData.normalization.baseYear}
                onChange={(e) => handleNormalizationChange('baseYear', parseInt(e.target.value) || 2023)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="2020"
                max="2030"
              />
            </div>
          </div>
        </div>

        {/* KPIs Grid */}
        <div className="space-y-6">
          {formData.kpis.map((kpi) => (
            <div key={kpi.id} className={`bg-gray-50 rounded-lg p-6 ${getErrorClass(kpi.id)}`}>
              {/* KPI Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{kpi.name}</h3>
                  <p className="text-gray-600">{kpi.description}</p>
                </div>
                {kpi.isCustom && (
                  <button
                    onClick={() => removeCustomKPI(kpi.id)}
                    className="text-red-600 hover:text-red-800 text-sm underline"
                  >
                    {t('workflow.calculation.kpis.remove')}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Values */}
                <div className="bg-white p-4 rounded border">
                  <h4 className="font-medium text-gray-800 mb-3">{t('workflow.calculation.kpis.kpi.currentPerformance')}</h4>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('workflow.calculation.kpis.kpi.currentValue')}</span>
                      <div className="text-right">
                        <span className="font-semibold text-gray-900">
                          {kpi.currentValue.toLocaleString()}
                        </span>
                        <span className="text-gray-500 ml-1">{kpi.unit}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('workflow.calculation.kpis.kpi.baseYear', { year: formData.normalization.baseYear })}</span>
                      <div className="text-right">
                        <span className="font-semibold text-gray-900">
                          {kpi.baseYearValue.toLocaleString()}
                        </span>
                        <span className="text-gray-500 ml-1">{kpi.unit}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('workflow.calculation.kpis.kpi.change')}</span>
                      <div className={`text-right font-semibold ${getTrendColor(kpi.currentValue, kpi.baseYearValue)}`}>
                        {getTrendIcon(kpi.currentValue, kpi.baseYearValue)}
                        {((kpi.currentValue - kpi.baseYearValue) / kpi.baseYearValue * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Targets & Trend */}
                <div className="bg-white p-4 rounded border">
                  <h4 className="font-medium text-gray-800 mb-3">{t('workflow.calculation.kpis.kpi.targetsTrend')}</h4>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('workflow.calculation.kpis.kpi.target', { year: kpi.targetYear })}</span>
                      <div className="text-right">
                        <span className="font-semibold text-gray-900">
                          {kpi.target.toLocaleString()}
                        </span>
                        <span className="text-gray-500 ml-1">{kpi.unit}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('workflow.calculation.kpis.kpi.progressToTarget')}</span>
                      <div className="text-right">
                        <span className={`font-semibold ${
                          kpi.currentValue <= kpi.target ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {((kpi.baseYearValue - kpi.currentValue) / (kpi.baseYearValue - kpi.target) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Mini Sparkline/Table */}
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">{t('workflow.calculation.kpis.kpi.trend', { startYear: formData.normalization.baseYear - 3, endYear: formData.normalization.baseYear })}</h5>
                    <div className="bg-gray-50 rounded p-2">
                      <div className="flex justify-between text-xs text-gray-600">
                        {kpi.trend.map((value, index) => (
                          <div key={index} className="text-center">
                            <div className="font-medium">{value.toLocaleString()}</div>
                            <div>{kpi.years[index]}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Availability & Assumptions */}
              <div className="mt-4">
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`w-3 h-3 rounded-full ${kpi.dataAvailable ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                  <span className="text-sm font-medium text-gray-700">
                    {kpi.dataAvailable ? t('workflow.calculation.kpis.kpi.dataAvailable') : t('workflow.calculation.kpis.kpi.dataNotAvailable')}
                  </span>
                </div>
                
                {!kpi.dataAvailable && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <label className="block text-sm font-medium text-yellow-900 mb-1">{t('workflow.calculation.kpis.kpi.assumptionRequired')}</label>
                    <textarea
                      value={kpi.assumption}
                      onChange={(e) => handleAssumptionChange(kpi.id, e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                        errors[kpi.id] ? 'border-red-500' : 'border-yellow-300'
                      }`}
                      placeholder={t('workflow.calculation.kpis.kpi.assumptionPlaceholder')}
                      rows={2}
                    />
                    {errors[kpi.id] && (
                      <p className="text-sm text-red-600 mt-1 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {errors[kpi.id]}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add Custom KPI */}
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6">
          <button
            onClick={addCustomKPI}
            className="w-full flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-800 py-4"
          >
            <BarChart3 className="w-5 h-5" />
            <span>{t('workflow.calculation.kpis.addCustomKPI')}</span>
          </button>
        </div>

        {/* Validation Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-red-800 font-medium mb-2">Errors</h4>
            <ul className="space-y-1">
              {Object.values(errors).map((error, index) => (
                <li key={index} className="text-red-700 text-sm flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {Object.keys(warnings).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-yellow-800 font-medium mb-2">Warnings</h4>
            <ul className="space-y-1">
              {Object.values(warnings).map((warning, index) => (
                <li key={index} className="text-yellow-700 text-sm flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Methodology Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-2">{t('workflow.calculation.kpis.methodology.title')}</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Energy Intensity:</strong> {t('workflow.calculation.kpis.methodology.energyIntensity')}</p>
            <p><strong>Carbon Intensity:</strong> {t('workflow.calculation.kpis.methodology.carbonIntensity')}</p>
            <p><strong>H&S Injury Rate:</strong> {t('workflow.calculation.kpis.methodology.hsInjuryRate')}</p>
            <p><strong>Custom KPIs:</strong> {t('workflow.calculation.kpis.methodology.customKPIs')}</p>
          </div>
        </div>

        {/* Navigation handled by WorkflowTabView */}
      </div>
    </div>
  );
};

export default KPIs;
